// Phase 5C — Intake-side pricing preview.
// Mirrors the Phase 3.5 calculator (suggestedSellHybrid) so the user sees
// what the saved row WILL look like — but is strictly DISPLAY-ONLY.
//
// CRITICAL: This component does NOT set unit_sell, manual_price_locked, or
// any pricing field. The vendor cost still lands in `unit_cost` only and the
// engine derives the suggested sell on the saved row. No Phase 3.5 contracts
// are touched.

import { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useAuth } from '@/contexts/AuthContext';
import { useShopPricingConfig, useShopPricingSettings } from '@/hooks/pricing/useShopPricingConfig';
import { suggestedSellHybrid, round2 } from '@/lib/parts-financial/calculator';

const fmt = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

interface Props {
  qty: number;
  unitCost: number | null;
  category?: string | null;
}

export function IntakePricingPreview({ qty, unitCost, category }: Props) {
  const matrixEnabled = useFeatureFlag('partsPricingMatrix');
  const pricingEnabled = useFeatureFlag('partsPricing');
  const { activeShop } = useAuth();
  const { config } = useShopPricingConfig(matrixEnabled ? activeShop?.id : undefined);
  const settingsQ = useShopPricingSettings(activeShop?.id);

  const cost = unitCost ?? 0;
  const safeQty = Math.max(1, Number(qty || 1));
  const mode = settingsQ.data?.matrix_quantity_mode === 'grouped' ? 'grouped' : 'per_unit';
  const minGpWarn = Number(settingsQ.data?.min_gp_warning_pct ?? 30);

  const hybrid = useMemo(
    () =>
      pricingEnabled && matrixEnabled && cost > 0
        ? suggestedSellHybrid(cost, category ?? null, config, { quantity: safeQty, quantityMode: mode })
        : null,
    [pricingEnabled, matrixEnabled, cost, category, config, safeQty, mode],
  );

  if (!pricingEnabled) return null;

  if (cost <= 0) {
    return (
      <div className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
        Pricing preview will appear once a unit cost is entered.
      </div>
    );
  }

  const suggestedEach = hybrid?.suggestedEach ?? cost;
  const suggestedTotal = hybrid?.suggestedTotal ?? round2(suggestedEach * safeQty);
  const totalCost = round2(cost * safeQty);
  const gpDollars = round2(suggestedTotal - totalCost);
  const gpPercent = suggestedEach > 0 ? round2(((suggestedEach - cost) / suggestedEach) * 100) : 0;
  const belowGp = gpPercent < minGpWarn;

  return (
    <div className="rounded-md border bg-muted/20 px-3 py-2.5 text-xs space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="font-medium text-foreground text-[11px] uppercase tracking-wide">
          Pricing preview
        </div>
        <div className="flex items-center gap-1">
          {mode === 'grouped' && (
            <Badge variant="outline" className="text-[9px] py-0 h-4">grouped</Badge>
          )}
          {hybrid?.clampedBy && (
            <Badge variant="outline" className="text-[9px] py-0 h-4">
              clamped: {hybrid.clampedBy}
            </Badge>
          )}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 tabular-nums">
        <PreviewCell label="Cost ea" value={fmt(cost)} />
        <PreviewCell label="Sell ea" value={fmt(suggestedEach)} />
        <PreviewCell label="Total" value={fmt(suggestedTotal)} />
        <PreviewCell
          label="GP"
          value={`${fmt(gpDollars)} · ${gpPercent.toFixed(0)}%`}
          warn={belowGp}
        />
      </div>
      {belowGp && (
        <div className="text-[10px] text-warning">
          ⚠ Margin below {minGpWarn}% threshold — preview only, won't block save.
        </div>
      )}
      <div className="text-[10px] text-muted-foreground opacity-70">
        Display only — engine recalculates the sell price on save.
      </div>
    </div>
  );
}

function PreviewCell({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={warn ? 'font-medium text-warning' : 'font-medium text-foreground'}>{value}</div>
    </div>
  );
}
